import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import {
  defaultLandingForProfile,
  hasRole,
  isReadOnly,
  useSession,
  type UserRole,
} from './SessionProvider';
import { canSeeMenu, useMenuPermissions } from './useMenuPermissions';
import type { MenuKey } from './menuKeys';
import { SplashScreen } from '@/shared/components/brand/SplashScreen';

export function ProtectedRoute({
  children,
  allowedRoles,
  menuKey,
  denyReadOnly,
  readOnlyFallback,
}: {
  children: ReactNode;
  allowedRoles?: UserRole[];
  /**
   * DB-driven revoke gate. When set, this route is reachable only if
   * the user passes BOTH `allowedRoles` AND the `menu_permissions` row
   * for this menuKey. `allowedRoles` stays as the defense-in-depth
   * upper bound — a Supabase edit can revoke access but cannot grant
   * beyond what the code permits. Use on top-level module routes only;
   * sub-routes (e.g. /ventas/nueva, /administracion/pagar/:compraId)
   * encode per-flow nuance and stay role-only.
   *
   * Footgun: if the bounce target (`defaultLandingForProfile`) is also
   * DB-hidden for this user, they end up in a redirect loop. Edit
   * `menu_permissions` carefully; the Phase 4 admin UI should validate
   * before saving.
   */
  menuKey?: MenuKey;
  /**
   * When true, the visitante (read-only) role is redirected away from this
   * route. Use for pages whose entire purpose is mutation (create/edit
   * forms, action handlers). `readOnlyFallback` controls where they land;
   * defaults to the parent listing as inferred from the URL.
   */
  denyReadOnly?: boolean;
  readOnlyFallback?: string;
}) {
  const { profile, loading } = useSession();
  const { visibility } = useMenuPermissions();
  const location = useLocation();

  if (loading) {
    return <SplashScreen />;
  }

  if (!profile) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allowedRoles && !hasRole(profile, allowedRoles)) {
    // Bounce to the user's natural landing — mantenimiento has no
    // Inicio access, so a flat `/` redirect would leave them on a
    // page they can't see in the nav. `defaultLandingForProfile`
    // routes mantenimiento to /mantenimiento; everyone else gets /.
    return <Navigate to={defaultLandingForProfile(profile)} replace />;
  }

  if (menuKey && !canSeeMenu(profile, visibility, menuKey)) {
    return <Navigate to={defaultLandingForProfile(profile)} replace />;
  }

  if (denyReadOnly && isReadOnly(profile)) {
    return <Navigate to={readOnlyFallback ?? '/'} replace />;
  }

  return <>{children}</>;
}
